import React, { useState, useEffect, useCallback } from 'react';
import { apiService, Business, BusinessData, BusinessLocationItem, BUSINESS_CATEGORIES, BusinessCategory } from '../services/api';
import { uploadImageToS3, isS3Configured } from '../services/s3Upload';
import { DragDropImageUpload } from './DragDropImageUpload';
import PlacesAutocomplete from './PlacesAutocomplete';
import './BusinessDetailsForm.css';

interface BusinessFormData {
  name: string;
  description: string;
  image: string;
  cover_image: string;
  category: BusinessCategory | '';
  email_domain: string;
}

// Normalize a user-entered email domain: trim, lowercase, drop a leading "@"
const normalizeEmailDomain = (raw: string): string =>
  raw.trim().toLowerCase().replace(/^@/, '');

interface LocationFormData {
  address: string;
  latitude: string;
  longitude: string;
}

const emptyBusinessForm: BusinessFormData = {
  name: '',
  description: '',
  image: '',
  cover_image: '',
  category: '',
  email_domain: ''
};

const emptyLocationForm: LocationFormData = {
  address: '',
  latitude: '',
  longitude: ''
};

const BusinessDetailsForm: React.FC = () => {
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Business form state
  const [businessForm, setBusinessForm] = useState<BusinessFormData>(emptyBusinessForm);
  const [previewImage, setPreviewImage] = useState('');
  const [previewCover, setPreviewCover] = useState('');
  
  // Location form state
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [locationForm, setLocationForm] = useState<LocationFormData>(emptyLocationForm);
  const [editingLocationIndex, setEditingLocationIndex] = useState<number | null>(null);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);
  const [isVirtualOnly, setIsVirtualOnly] = useState(false);
  
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    fetchBusiness();
  }, []);

  const fetchBusiness = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getBusiness();
      if (data) {
        setBusiness(data);
        // Populate form with existing data
        setBusinessForm({
          name: data.name || '',
          description: data.description || '',
          image: data.image || '',
          cover_image: data.cover_image || '',
          category: data.category || '',
          email_domain: data.email_domain || ''
        });
        setPreviewImage(data.image || '');
        setPreviewCover(data.cover_image || '');
        
        // Check if business is virtual only (has a location marked as Virtual Business)
        const hasVirtualLocation = data.locations?.some(
          loc => loc.address?.toLowerCase().includes('virtual business')
        );
        setIsVirtualOnly(hasVirtualLocation || false);
      } else {
        setBusiness(null);
      }
    } catch (err) {
      console.error('Failed to fetch business:', err);
      setBusiness(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBusinessForm(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleImageUpload = async (file: File, field: 'image' | 'cover_image') => {
    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      if (field === 'image') {
        setPreviewImage(result);
      } else {
        setPreviewCover(result);
      }
    };
    reader.readAsDataURL(file);

    // Upload to S3
    const setUploading = field === 'image' ? setUploadingImage : setUploadingCover;
    setUploading(true);
    setError(null);

    try {
      if (!isS3Configured()) {
        throw new Error('S3 is not configured. Please check your environment variables.');
      }

      const s3Url = await uploadImageToS3(file);
      
      // Update form with S3 URL
      setBusinessForm(prev => ({ ...prev, [field]: s3Url }));
      setHasChanges(true);
      showSuccessMessage(`${field === 'image' ? 'Logo' : 'Cover image'} uploaded successfully`);
    } catch (err: any) {
      console.error('Image upload failed:', err);
      setError(err.message || 'Failed to upload image. Please try again.');
      // Reset preview on failure
      if (field === 'image') {
        setPreviewImage(businessForm.image || '');
      } else {
        setPreviewCover(businessForm.cover_image || '');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = (file: File) => handleImageUpload(file, 'image');
  const handleCoverUpload = (file: File) => handleImageUpload(file, 'cover_image');

  const showSuccessMessage = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleSaveMetadata = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Validate required images when creating a new business
    if (!business) {
      if (!businessForm.image) {
        setError('Business logo is required');
        setSubmitting(false);
        return;
      }
      if (!businessForm.cover_image) {
        setError('Cover image is required');
        setSubmitting(false);
        return;
      }
    }

    const businessData: BusinessData = {
      name: businessForm.name,
      description: businessForm.description || undefined,
      // Images are now S3 URLs, not base64
      image: businessForm.image || '',
      cover_image: businessForm.cover_image || '',
      category: businessForm.category || undefined,
      // null clears the domain (open signup); a value enforces it
      email_domain: normalizeEmailDomain(businessForm.email_domain) || null,
      locations: business?.locations || []
    };

    try {
      if (business) {
        // Update existing business
        const updated = await apiService.updateBusiness(businessData);
        setBusiness(updated);
        showSuccessMessage('Business details saved');
      } else {
        // Create new business
        const created = await apiService.createBusiness(businessData);
        setBusiness(created);
        // Populate form with response data
        setBusinessForm({
          name: created.name || '',
          description: created.description || '',
          image: created.image || '',
          cover_image: created.cover_image || '',
          category: created.category || '',
          email_domain: created.email_domain || ''
        });
        showSuccessMessage('Business created successfully');
      }
      setHasChanges(false);
    } catch (err: any) {
      console.error('Failed to save business:', err);
      setError(err.message || 'Failed to save business. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Location management
  const handleLocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocationForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (value: string) => {
    setLocationForm(prev => ({ ...prev, address: value }));
  };

  const handlePlaceSelect = useCallback((place: { address: string; latitude: number; longitude: number }) => {
    setLocationForm({
      address: place.address,
      latitude: place.latitude.toString(),
      longitude: place.longitude.toString()
    });
  }, []);

  const handleAddLocation = () => {
    setLocationForm(emptyLocationForm);
    setEditingLocationIndex(null);
    setShowLocationForm(true);
  };

  const handleVirtualOnlyToggle = async (checked: boolean) => {
    if (!business) return;
    
    setIsVirtualOnly(checked);
    setSubmitting(true);
    setError(null);

    let updatedLocations: BusinessLocationItem[];
    
    if (checked) {
      // Set to virtual only - use 0,0 coordinates
      updatedLocations = [{
        address: 'Virtual Business (Online Only)',
        latitude: 0,
        longitude: 0
      }];
    } else {
      // Remove the virtual location, business will need to add physical locations
      updatedLocations = [];
    }

    const businessData: BusinessData = {
      name: business.name,
      description: business.description,
      image: business.image || '',
      cover_image: business.cover_image || '',
      email_domain: business.email_domain,
      locations: updatedLocations
    };

    try {
      const updated = await apiService.updateBusiness(businessData);
      setBusiness(updated);
      showSuccessMessage(checked ? 'Business set to virtual only' : 'Virtual mode disabled - add physical locations');
      setShowLocationForm(false);
    } catch (err: any) {
      console.error('Failed to update virtual status:', err);
      setError(err.message || 'Failed to update business. Please try again.');
      setIsVirtualOnly(!checked); // Revert on error
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditLocation = (index: number) => {
    const loc = business?.locations?.[index];
    if (loc) {
      setLocationForm({
        address: loc.address,
        latitude: loc.latitude.toString(),
        longitude: loc.longitude.toString()
      });
      setEditingLocationIndex(index);
      setShowLocationForm(true);
    }
  };

  const handleSaveLocation = async () => {
    if (!business) return;
    
    setSubmitting(true);
    setError(null);

    let updatedLocations: BusinessLocationItem[];
    if (editingLocationIndex !== null) {
      // When editing, preserve the existing location's ID
      updatedLocations = (business.locations || []).map((loc, i) => {
        if (i === editingLocationIndex) {
          return {
            ...loc, // Preserve existing properties like id
            address: locationForm.address,
            latitude: parseFloat(locationForm.latitude) || 0,
            longitude: parseFloat(locationForm.longitude) || 0
          };
        }
        return loc;
      });
    } else {
      // When adding new, create without id (backend will assign)
      const newLocation: BusinessLocationItem = {
        address: locationForm.address,
        latitude: parseFloat(locationForm.latitude) || 0,
        longitude: parseFloat(locationForm.longitude) || 0
      };
      updatedLocations = [...(business.locations || []), newLocation];
    }

    const businessData: BusinessData = {
      name: business.name,
      description: business.description,
      // Images are now S3 URLs, not base64
      image: business.image || '',
      cover_image: business.cover_image || '',
      email_domain: business.email_domain,
      locations: updatedLocations
    };

    try {
      const updated = await apiService.updateBusiness(businessData);
      setBusiness(updated);
      showSuccessMessage(editingLocationIndex !== null ? 'Location updated' : 'Location added');
      setLocationForm(emptyLocationForm);
      setEditingLocationIndex(null);
      setShowLocationForm(false);
    } catch (err: any) {
      console.error('Failed to save location:', err);
      setError(err.message || 'Failed to save location. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLocation = async (index: number) => {
    if (!business) return;
    
    setSubmitting(true);
    const updatedLocations = (business.locations || []).filter((_, i) => i !== index);

    const businessData: BusinessData = {
      name: business.name,
      description: business.description,
      // Images are now S3 URLs, not base64
      image: business.image || '',
      cover_image: business.cover_image || '',
      email_domain: business.email_domain,
      locations: updatedLocations
    };

    try {
      const updated = await apiService.updateBusiness(businessData);
      setBusiness(updated);
      showSuccessMessage('Location removed');
      setDeleteConfirmIndex(null);
    } catch (err: any) {
      console.error('Failed to delete location:', err);
      setError(err.message || 'Failed to delete location. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelLocation = () => {
    setLocationForm(emptyLocationForm);
    setEditingLocationIndex(null);
    setShowLocationForm(false);
    setError(null);
  };

  if (loading) {
    return (
      <div className="business-details-form">
        <div className="loading-state">
          <p>Loading your business...</p>
        </div>
      </div>
    );
  }

  // No business exists - show setup form
  if (!business) {
    return (
      <div className="business-details-form">
        <div className="info-card">
          <div className="info-card-icon">🏪</div>
          <div className="info-card-content">
            <h3>Business Details</h3>
            <p>Set up your business profile to get started.</p>
          </div>
        </div>

        <div className="setup-container">
          <div className="setup-welcome">
            <div className="setup-icon">🚀</div>
            <h2>Set Up Your Business</h2>
            <p>Create your business profile. You can add store locations after.</p>
          </div>
          
          <form onSubmit={handleSaveMetadata} className="business-setup-form">
            {error && <div className="error-message">⚠ {error}</div>}
            
            <div className="form-section">
              <div className="form-section-title">Business Information</div>
              <div className="form-group">
                <label htmlFor="name">Business Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={businessForm.name}
                  onChange={handleBusinessInputChange}
                  placeholder="e.g., Joe's Coffee Shop"
                  required
                  disabled={submitting}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={businessForm.description}
                  onChange={handleBusinessInputChange}
                  placeholder="Tell customers what makes your business special..."
                  disabled={submitting}
                  rows={3}
                  maxLength={500}
                />
                <div className="char-counter">
                  {businessForm.description.length}/500 characters
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={businessForm.category}
                  onChange={handleBusinessInputChange}
                  disabled={submitting}
                  className="category-select"
                >
                  <option value="">Select a category...</option>
                  {BUSINESS_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">Images</div>
              <div className="form-row">
                <div className="form-group">
                  <DragDropImageUpload
                    label="Business Logo *"
                    onFileSelect={handleLogoUpload}
                    previewUrl={previewImage}
                    uploading={uploadingImage}
                    placeholder="Drop logo here or click to browse"
                    accept="image/png,image/jpeg,image/jpg"
                  />
                </div>
                <div className="form-group">
                  <DragDropImageUpload
                    label="Cover Image *"
                    onFileSelect={handleCoverUpload}
                    previewUrl={previewCover}
                    uploading={uploadingCover}
                    placeholder="Drop cover image here or click to browse"
                    accept="image/png,image/jpeg,image/jpg"
                  />
                </div>
              </div>
            </div>

            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Business'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Business exists - show metadata form + locations table
  return (
    <div className="business-details-form">
      {/* Info Card */}
      <div className="info-card">
        <div className="info-card-icon">🏪</div>
        <div className="info-card-content">
          <h3>Business Details</h3>
          <p>Manage your business profile and store locations.</p>
        </div>
      </div>

      {success && <div className="success-message">✓ {success}</div>}
      {error && <div className="error-message">⚠ {error}</div>}

      {/* Business Metadata Section */}
      <div className="metadata-section">
        <div className="section-header">
          <h3>📋 Business Information</h3>
          {hasChanges && <span className="unsaved-badge">Unsaved changes</span>}
        </div>
        
        <form onSubmit={handleSaveMetadata} className="metadata-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Business Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={businessForm.name}
                onChange={handleBusinessInputChange}
                placeholder="e.g., Joe's Coffee Shop"
                required
                disabled={submitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={businessForm.description}
                onChange={handleBusinessInputChange}
                placeholder="Tell customers what makes your business special..."
                disabled={submitting}
                rows={2}
                maxLength={500}
              />
              <div className="char-counter">
                {businessForm.description.length}/500 characters
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="edit-category">Category</label>
              <select
                id="edit-category"
                name="category"
                value={businessForm.category}
                onChange={handleBusinessInputChange}
                disabled={submitting}
                className="category-select"
              >
                <option value="">Select a category...</option>
                {BUSINESS_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <DragDropImageUpload
                label="Business Logo *"
                onFileSelect={handleLogoUpload}
                previewUrl={previewImage}
                uploading={uploadingImage}
                placeholder="Drop logo here or click"
                accept="image/png,image/jpeg,image/jpg"
                compact
              />
            </div>
            <div className="form-group">
              <DragDropImageUpload
                label="Cover Image *"
                onFileSelect={handleCoverUpload}
                previewUrl={previewCover}
                uploading={uploadingCover}
                placeholder="Drop cover here or click"
                accept="image/png,image/jpeg,image/jpg"
                compact
              />
            </div>
          </div>

          <div className="form-actions inline">
            <button type="submit" className="save-button" disabled={submitting || !hasChanges}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Locations Section */}
      <div className="locations-section">
        <div className="section-header">
          <h3>📍 Store Locations</h3>
          {!isVirtualOnly && (
            <button className="add-button" onClick={handleAddLocation} disabled={showLocationForm || submitting}>
              + Add Location
            </button>
          )}
        </div>

        {/* Virtual Only Toggle */}
        <div className="virtual-toggle-section">
          <label className="virtual-toggle-wrapper">
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={isVirtualOnly}
                onChange={(e) => handleVirtualOnlyToggle(e.target.checked)}
                disabled={submitting}
              />
              <span className="toggle-slider"></span>
            </div>
            <div className="toggle-content">
              <span className="toggle-label-text">Virtual Business Only</span>
              <span className="toggle-description">
                {isVirtualOnly 
                  ? '🌐 This business operates online only (no physical locations)' 
                  : 'Enable if this business has no physical storefront'}
              </span>
            </div>
          </label>
        </div>

        {/* Virtual Only Message */}
        {isVirtualOnly && (
          <div className="virtual-only-message">
            <div className="virtual-icon">🌐</div>
            <div className="virtual-content">
              <h4>Virtual Business</h4>
              <p>This business operates online only. Customers can access offers from anywhere.</p>
            </div>
          </div>
        )}

        {/* Add/Edit Location Form */}
        {!isVirtualOnly && showLocationForm && (
          <div className="location-form-inline">
            <div className="form-group full-width">
              <label htmlFor="address">Search Address *</label>
              <PlacesAutocomplete
                id="address"
                value={locationForm.address}
                onChange={handleAddressChange}
                onPlaceSelect={handlePlaceSelect}
                placeholder="Start typing an address..."
                disabled={submitting}
                required
              />
              <span className="field-hint">Start typing and select from suggestions. Coordinates will auto-fill.</span>
            </div>
            <div className="form-row two-col">
              <div className="form-group">
                <label htmlFor="latitude">Latitude</label>
                <input
                  type="text"
                  id="latitude"
                  name="latitude"
                  value={locationForm.latitude}
                  readOnly
                  className="readonly-input"
                  placeholder="Auto-filled"
                />
              </div>
              <div className="form-group">
                <label htmlFor="longitude">Longitude</label>
                <input
                  type="text"
                  id="longitude"
                  name="longitude"
                  value={locationForm.longitude}
                  readOnly
                  className="readonly-input"
                  placeholder="Auto-filled"
                />
              </div>
            </div>
            <div className="form-actions inline">
              <button type="button" className="cancel-button" onClick={handleCancelLocation} disabled={submitting}>
                Cancel
              </button>
              <button 
                type="button" 
                className="save-button" 
                onClick={handleSaveLocation} 
                disabled={submitting || !locationForm.latitude || !locationForm.longitude}
              >
                {submitting ? 'Saving...' : (editingLocationIndex !== null ? 'Update Location' : 'Add Location')}
              </button>
            </div>
          </div>
        )}

        {/* Locations Table - Only show when not virtual only */}
        {!isVirtualOnly && (
          (!business.locations || business.locations.length === 0 || (business.locations.length === 1 && business.locations[0].address?.toLowerCase().includes('virtual business'))) ? (
            <div className="empty-state">
              <p>No locations added yet.</p>
              {!showLocationForm && (
                <button className="add-first-button" onClick={handleAddLocation}>
                  + Add Your First Location
                </button>
              )}
            </div>
          ) : (
            <table className="locations-table">
            <thead>
              <tr>
                <th>Address</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {business.locations
                .map((loc, originalIndex) => ({ loc, originalIndex }))
                .filter(({ loc }) => !loc.address?.toLowerCase().includes('virtual business'))
                .map(({ loc, originalIndex }) => (
                <tr key={originalIndex}>
                  <td>{loc.address}</td>
                  <td>{loc.latitude.toFixed(6)}</td>
                  <td>{loc.longitude.toFixed(6)}</td>
                  <td className="actions-cell">
                    {deleteConfirmIndex === originalIndex ? (
                      <>
                        <button 
                          className="action-btn confirm-btn"
                          onClick={() => handleDeleteLocation(originalIndex)}
                          disabled={submitting}
                        >
                          Confirm
                        </button>
                        <button 
                          className="action-btn cancel-btn"
                          onClick={() => setDeleteConfirmIndex(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          className="action-btn edit-btn"
                          onClick={() => handleEditLocation(originalIndex)}
                          disabled={showLocationForm}
                        >
                          Edit
                        </button>
                        <button 
                          className="action-btn delete-btn"
                          onClick={() => setDeleteConfirmIndex(originalIndex)}
                          disabled={showLocationForm}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )
        )}
      </div>
    </div>
  );
};

export default BusinessDetailsForm;
